/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Manages user session, login, logout, and profile information.
 *
 * @module contexts/AuthContext
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/supabase';
import { revokeKaneLink } from '../services/kaneAuth';

const AuthContext = createContext({});

/**
 * Custom hook to access authentication context
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the application to provide authentication state
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True when the session expired without an explicit signOut() call
  const [sessionExpired, setSessionExpired] = useState(false);
  const profileFetchRef = useRef(null); // Dedup in-flight profile fetches
  // Track whether the user was previously authenticated (to detect silent expiry)
  const wasAuthenticatedRef = useRef(false);
  // Track whether the current sign-out was initiated by the user (via signOut())
  const intentionalSignOutRef = useRef(false);

  /**
   * Fetch user profile from database (deduplicated)
   * @param {string} userId - User ID
   */
  const fetchUserProfile = useCallback(async (userId) => {
    // Reuse in-flight request for the same user
    if (profileFetchRef.current?.userId === userId && profileFetchRef.current?.promise) {
      return profileFetchRef.current.promise;
    }

    const promise = (async () => {
      try {
        const { profile, error } = await authService.getUserProfile(userId);
        if (error) throw error;
        setProfile(profile);
      } catch (error) {
        console.error('Fetch profile error:', error);
        setError(error);
      } finally {
        profileFetchRef.current = null;
      }
    })();

    profileFetchRef.current = { userId, promise };
    return promise;
  }, []);

  /**
   * Check for existing session on initial load
   */
  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      const { session, error } = await authService.getSession();

      if (error) throw error;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        await fetchUserProfile(session.user.id);
      }
    } catch (error) {
      console.error('Session check error:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  /**
   * Initialize authentication state on mount
   */
  useEffect(() => {
    // Check for existing session
    checkSession();

    // Subscribe to auth state changes
    const { data: authListener } = authService.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          wasAuthenticatedRef.current = true;
          setSessionExpired(false);
          // Fetch user profile when authenticated
          await fetchUserProfile(session.user.id);
        } else {
          setProfile(null);
          // If the user was previously authenticated and this sign-out was NOT
          // intentional (i.e., triggered by session expiry), flag it.
          if (wasAuthenticatedRef.current && !intentionalSignOutRef.current) {
            setSessionExpired(true);
          }
          wasAuthenticatedRef.current = false;
          intentionalSignOutRef.current = false;
        }

        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [checkSession, fetchUserProfile]);

  /**
   * Sign in user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const { user, session, error } = await authService.signIn(email, password);

      if (error) throw error;

      setUser(user);
      setSession(session);

      if (user) {
        await fetchUserProfile(user.id);
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out current user
   *
   * El alcance por default es `local` (sólo esta sesión). Era global —el
   * default de supabase-js— y eso revocaba TODAS las sesiones de la persona:
   * cerrar sesión en el celular sacaba de la computadora, y además mataba el
   * connector de ProMedic en ChatGPT, que hasta v0.65.0 vivía de una sesión de
   * Supabase (ver `src/services/supabase.js`).
   *
   * `{ scope: 'global' }` se pasa EXPLÍCITO desde el único lugar que lo
   * promete: el botón "Cerrar todas las sesiones" del perfil.
   *
   * @param {Object} [opciones]
   * @param {'local'|'global'} [opciones.scope]
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  const signOut = async (opciones = {}) => {
    try {
      setLoading(true);
      setError(null);
      // Mark this as an intentional sign-out so the auth listener
      // doesn't treat the resulting SIGNED_OUT event as session expiry.
      intentionalSignOutRef.current = true;

      // Best-effort: tear down the persistent Kane SSO link before clearing
      // the Supabase session. Never throws — logout must always proceed
      // even if the mcp-server is unreachable. (#174)
      await revokeKaneLink();

      const { error } = await authService.signOut(opciones);

      if (error) throw error;

      setUser(null);
      setSession(null);
      setProfile(null);
      setSessionExpired(false);

      return { success: true, error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      intentionalSignOutRef.current = false;
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh current session
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  const refreshSession = async () => {
    try {
      setLoading(true);
      const { session, error } = await authService.refreshSession();

      if (error) throw error;

      setSession(session);
      setUser(session?.user ?? null);

      return { success: true, error: null };
    } catch (error) {
      console.error('Refresh session error:', error);
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update user profile
   * @param {Object} updates - Profile updates
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      setLoading(true);
      setError(null);

      const { profile, error } = await authService.updateUserProfile(user.id, updates);

      if (error) throw error;

      setProfile(profile);

      return { success: true, error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return profile?.role === role;
  };

  // Computed role booleans. Exposed as plain values (not functions) so
  // components can write `{isAdmin && ...}` without invoking a function
  // reference (which is always truthy).
  const isAdmin = profile?.role === 'admin';
  const isSupervisor = profile?.role === 'supervisor';
  const isTechnician = profile?.role === 'technician';
  const isClient = profile?.role === 'client';
  /** @deprecated Use isTechnician */
  const isEmployee = isTechnician;

  const requestPasswordReset = async (email) => {
    return authService.requestPasswordReset(email);
  };

  const updatePassword = async (newPassword) => {
    return authService.updatePassword(newPassword);
  };

  const value = {
    user,
    profile,
    session,
    loading,
    error,
    isAuthenticated: !!user,
    sessionExpired,
    signIn,
    signOut,
    refreshSession,
    updateProfile,
    requestPasswordReset,
    updatePassword,
    hasRole,
    isAdmin,
    isSupervisor,
    isTechnician,
    isEmployee, // backward compat alias
    isClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
